import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, InputLabel, TextField, Typography, TextareaAutosize } from '@mui/material';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import styles from './Person.module.scss';
import { memo, useEffect, useState } from 'react';
import { cast, exportExcel, handleApiError, isTypeRoleId, isWard, isXaphuongTinhThanh, notify } from '~/utils/helper';
import { AddNewIcon, AddSquareIcon, CloseIcon, MessageIcon } from '~/assets/icons';
import { IPerson } from '~/utils/interface/person';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, PAGE_INDEX } from '~/utils/constants/common';
import dayjs from 'dayjs';
import BaseRow from '~/components/common/base-row/BaseRow';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import personApi from '~/apis/person';
import { IMasterDataItem } from '~/utils/interface/master';
import locationApi from '~/apis/location';
import AddImeiDialog from '~/pages/admin/devices/components/add-imei-dialog/AddImeiDialog';
import EditPersonDialog from './components/EditPersonDialog';
import AssignDeviceModal from './components/AssignDeviceModal';
import ExchangeDeviceModal from './components/ExchangeDeviceModal';
import UnassignDeviceModal from './components/UnassignDeviceModal';
import AssignLocationDialog from '~/components/location/AssignLocationDialog';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import { ActiveEnum, ImeiEnum, RoleIdUserEnum, StatusEnum } from '~/utils/enum';
import PersonList from './components/PersonList';
import { handleGetPersonList } from '~/thunks/person/personThunk';
import { personActions } from '~/thunks/person/personSlice';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import UnAssignLocationDialog from '~/components/location/UnAssignLocationDialog';
import { useCallContext } from '~/context/CallContext';

const cx = classNames.bind(styles);

const Person = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/person');

  const { filter, needRefreshData } = useAppSelector((state) => state.person);

  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const currentUser = Object.values(selectedUser).length === 0 ? userProfile : selectedUser;

  const [editData, setEditData] = useState<IPerson>();

  const [isOpenEditPersonDialog, setIsOpenEditPersonDialog] = useState<boolean>(false);
  const [editPersonDialogMode, setEditPersonDialogMode] = useState<string>(DIALOG_MODE_CREATE);
  const [isOpenAddImeiDialog, setIsOpenAddImeiDialog] = useState<boolean>(false);
  const [isOpenAssignDeviceModal, setIsOpenAssignDeviceModal] = useState(false);
  const [isOpenExchangeDeviceModal, setIsOpenExchangeDeviceModal] = useState(false);
  const [isOpenUnassignDeviceModal, setIsOpenUnassignDeviceModal] = useState(false);
  const [assignDevicePerson, setAssignDevicePerson] = useState<any | null>(null);

  const [isOpenAssignLocationDialog, setIsOpenAssignLocationDialog] = useState<boolean>(false);
  const [isOpenUnAssignLocationDialog, setIsOpenUnAssignLocationDialog] = useState<boolean>(false);
  const [isOpenConfirmActivePersonDialog, setIsOpenConfirmActivePersonDialog] = useState<boolean>(false);
  const [isOpenMessageDialog, setIsOpenMessageDialog] = useState<boolean>(false);
  const [messageText, setMessageText] = useState<string>('');
  const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false);
  const [isExportingExcel, setIsExportingExcel] = useState<boolean>(false);
  const [sendMessageType, setSendMessageType] = useState<'single' | 'all'>('single');
  const [messageTargetPerson, setMessageTargetPerson] = useState<IPerson | undefined>(undefined);
  const { openCallDeviceModal } = useCallContext();

  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);

  const assignStatusOptions = [
    {
      id: ImeiEnum.UNASSIGNED,
      name: t('assignStatus.' + ImeiEnum.UNASSIGNED),
    },
    {
      id: ImeiEnum.ASSIGNED,
      name: t('assignStatus.' + ImeiEnum.ASSIGNED),
    },
  ];

  const geofenceStatusOptions = [
    {
      id: StatusEnum.INACTIVE,
      name: t('geofenceStatus.' + StatusEnum.INACTIVE),
    },
    {
      id: StatusEnum.ACTIVE,
      name: t('geofenceStatus.' + StatusEnum.ACTIVE),
    },
  ];

  const statusOptions = [
    {
      id: StatusEnum.INACTIVE,
      name: t('status.' + StatusEnum.INACTIVE),
    },
    {
      id: StatusEnum.ACTIVE,
      name: t('status.' + StatusEnum.ACTIVE),
    },
  ];

  const isXaPhuongRole = isTypeRoleId(cast(currentUser?.roleid), RoleIdUserEnum.XA_PHUONG);

  useEffect(() => {
    if (!currentUser?.username) return;

    dispatch(handleGetPersonList({ ...filter, username: currentUser?.username }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData, currentUser?.username]);

  useEffect(() => {
    getListProvinces();
  }, []);

  useEffect(() => {
    const provinceId = filter.provinceId || currentUser?.province_id;
    if (!provinceId) {
      setWardOptions([]);
      return;
    }

    (async () => {
      try {
        const response = await locationApi.getListWards({
          province: cast(provinceId),
        });
        setWardOptions(
          response.data.map((item) => {
            return {
              id: item.code,
              name: item.name,
              data0: item.name,
              data1: item.name_with_type,
              data2: item.type,
              data3: item.parent_code,
            };
          })
        );
      } catch (error) {
        setWardOptions([]);
        handleApiError(error);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.provinceId, currentUser?.province_id]);

  // Reset when change login
  useEffect(() => {
    return () => {
      dispatch(personActions.resetFilter());
      setWardOptions([]);
    };
  }, [dispatch]);

  const getListProvinces = async () => {
    try {
      const response = await locationApi.getListProvinces();
      setProvinceOptions(
        response.data.map((item) => {
          return {
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
          };
        })
      );
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleClickAddNew = () => {
    setIsOpenEditPersonDialog(true);
    setEditPersonDialogMode(DIALOG_MODE_CREATE);
  };

  const handleSetOpenEditDialog = (open: boolean, needRefreshData?: boolean) => {
    setIsOpenEditPersonDialog(open);

    if (needRefreshData) dispatch(personActions.changeNeedRefreshData(true));
  };

  const handleOpenAssignDeviceModal = (row: any) => {
    setAssignDevicePerson(row);
    setIsOpenAssignDeviceModal(true);
  };

  const handleCloseAssignDeviceModal = () => {
    setIsOpenAssignDeviceModal(false);
    setAssignDevicePerson(null);
  };

  const handleOpenExchangeDeviceModal = (row: any) => {
    setAssignDevicePerson(row);
    setIsOpenExchangeDeviceModal(true);
  };

  const handleCloseExchangeDeviceModal = () => {
    setIsOpenExchangeDeviceModal(false);
    setAssignDevicePerson(null);
  };

  const handleOpenUnassignDeviceModal = (row: any) => {
    setAssignDevicePerson(row);
    setIsOpenUnassignDeviceModal(true);
  };

  const handleCloseUnassignDeviceModal = () => {
    setIsOpenUnassignDeviceModal(false);
    setAssignDevicePerson(null);
  };

  const handleSetOpenAssignLocationDialog = (open: boolean, needRefreshData?: boolean) => {
    setIsOpenAssignLocationDialog(open);

    if (needRefreshData) dispatch(personActions.changeNeedRefreshData(true));
  };

  const handleSetOpenUnAssignLocationDialog = (open: boolean, needRefreshData?: boolean) => {
    setIsOpenUnAssignLocationDialog(open);

    if (needRefreshData) dispatch(personActions.changeNeedRefreshData(true));
  };

  const handleActivePerson = async () => {
    try {
      if (!editData) return;

      const currentStatus = Number(editData.active);
      const newStatus = currentStatus === ActiveEnum.OPEN ? ActiveEnum.BLOCKED : ActiveEnum.OPEN;
      await personApi.active(cast(editData.id), newStatus);

      dispatch(personActions.changeNeedRefreshData(true));

      notify(
        'success',
        currentStatus === ActiveEnum.OPEN
          ? t('active_person.open.success', { cccd: cast(editData.cccd) })
          : t('active_person.block.success', { cccd: cast(editData.cccd) })
      );

      setIsOpenConfirmActivePersonDialog(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleOpenEdit = async (row: IPerson) => {
    try {
      setEditData(row);
      setEditPersonDialogMode(DIALOG_MODE_EDIT);
      setIsOpenEditPersonDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleClearFilter = () => {
    dispatch(personActions.resetFilter());
  };

  const handleExportExcel = async () => {
    if (!currentUser?.username) return;

    try {
      setIsExportingExcel(true);
      notify('info', t('export.waiting'));

      const response = await personApi.export({
        ...filter,
        username: currentUser.username,
        provinceId: filter.provinceId || cast(currentUser?.province_id),
        wardId: filter.wardId || cast(currentUser?.ward_id),
        pageIndex: undefined,
        pageSize: undefined,
      });

      const rows = Array.isArray(response.data.result) ? response.data.result : [];
      const columns = rows.length
        ? Object.keys(rows[0]).map((key) => ({ header: key, key, width: 20 }))
        : [];

      await exportExcel(columns, rows, `persons_${dayjs().format('YYYYMMDD_HHmmss')}`);
      notify('success', t('export.success'));
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleOpenSendMessageSingle = (row: IPerson) => {
    if (!isXaPhuongRole) return;

    setMessageTargetPerson(row);
    setSendMessageType('single');
    setMessageText('');
    setIsOpenMessageDialog(true);
  };

  const handleOpenSendMessageAll = () => {
    if (!isXaPhuongRole) return;

    setMessageTargetPerson(undefined);
    setSendMessageType('all');
    setMessageText('');
    setIsOpenMessageDialog(true);
  };

  const handleCloseMessageDialog = () => {
    setIsOpenMessageDialog(false);
    setMessageText('');
    setMessageTargetPerson(undefined);
    setSendMessageType('single');
  };

  const handleSubmitSendMessage = async () => {
    if (!isXaPhuongRole) return;

    const message = messageText.trim();
    if (!message) {
      notify('warning', 'Vui long nhap noi dung tin nhan');
      return;
    }

    try {
      setIsSendingMessage(true);

      if (sendMessageType === 'single') {
        if (!messageTargetPerson?.id) {
          notify('warning', 'Khong tim thay nguoi nhan');
          return;
        }
        await personApi.sendMessage(cast(messageTargetPerson.id), { message });
        notify('success', 'Gui tin nhan thanh cong');
      } else {
        const username = cast(currentUser?.username);
        if (!username) {
          notify('warning', 'Khong tim thay tai khoan xa phuong');
          return;
        }
        await personApi.sendMessageAll(username, { message });
        notify('success', 'Gui tin nhan cho tat ca thanh cong');
      }

      handleCloseMessageDialog();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSendingMessage(false);
    }
  };

  return (
    <Box className={cx('person-screen')}>
      <Box className='flex justify-between items-center bg-[#123B6D] h-20 px-6'>
        <Typography className='text-white font-semibold text-xl'>{t('list')}</Typography>
        <Box className='flex items-center gap-3'>
          {isXaPhuongRole && (
            <Button
              className='bg-white mr-10 rounded-xl text-[#123B6D] h-12 hover:bg-white hover:opacity-80 text-base font-bold px-5'
              startIcon={<MessageIcon />}
              onClick={handleOpenSendMessageAll}
            >
              {t('message.title.all')}
            </Button>
          )}
          <Button
            className='bg-white rounded-xl text-[#123B6D] h-12 hover:bg-white hover:opacity-80 text-base font-bold px-5'
            onClick={handleExportExcel}
            disabled={isExportingExcel}
          >
            {isExportingExcel ? t('export.loading') : t('export.button')}
          </Button>
          <Button
            className='bg-white rounded-xl text-[#123B6D] h-12 hover:bg-white hover:opacity-80 text-base font-bold px-5'
            startIcon={<AddNewIcon />}
            onClick={handleClickAddNew}
          >
            {t('create')}
          </Button>
        </Box>
      </Box>
      <BaseRow className='gap-4 px-6 pt-6 mb-7 !items-end'>
        <Box className='w-full'>
          <BaseRow className='gap-4 mb-4 max-[1600px]:!flex-col'>
            <Box className='min-[1600px]:w-1/3 w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('province_id')}</Typography>
              <BaseSelect
                options={
                  isXaphuongTinhThanh(cast(currentUser?.roleid))
                    ? provinceOptions.filter((item: IMasterDataItem) => item.id === String(currentUser?.province_id))
                    : provinceOptions
                }
                value={filter.provinceId ? String(filter.provinceId) : String(currentUser?.province_id)}
                placeholder={t('select_province_id')}
                isDisplayAllOption
                isSearchEnable
                onChange={(e) =>
                  dispatch(personActions.setFilter({ provinceId: e.target.value, pageIndex: PAGE_INDEX }))
                }
                disabled={isXaphuongTinhThanh(cast(currentUser?.roleid))}
              />
            </Box>
            <Box className='min-[1600px]:w-1/3 w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('ward_id')}</Typography>
              <BaseSelect
                options={wardOptions}
                value={filter.wardId ? String(filter.wardId) : String(currentUser?.ward_id)}
                placeholder={t('select_ward_id')}
                isDisplayAllOption
                isSearchEnable
                onChange={(e) => dispatch(personActions.setFilter({ wardId: e.target.value, pageIndex: PAGE_INDEX }))}
                disabled={isWard(cast(currentUser?.roleid))}
              />
            </Box>
            <Box className='min-[1600px]:w-1/3 w-full'>
              <Box className='flex items-center mb-2'>
                <Typography className='text-[#161A1F] font-medium text-sm mr-2'>{t('assignStatus.title')}</Typography>
                {isTypeRoleId(cast(currentUser?.roleid), RoleIdUserEnum.QUAN_TRI) && (
                  <AddSquareIcon
                    className='hover:cursor-pointer w-5 h-5'
                    onClick={() => setIsOpenAddImeiDialog(true)}
                  />
                )}
              </Box>
              <BaseSelect
                options={assignStatusOptions}
                value={filter.assignStatus ?? ''}
                placeholder={t('select_assign_status')}
                isDisplayAllOption
                onChange={(e) => {
                  const value = e.target.value;
                  dispatch(
                    personActions.setFilter({
                      assignStatus: value === '' ? undefined : Number(value),
                      pageIndex: PAGE_INDEX,
                    })
                  );
                }}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-4 max-[1600px]:!flex-col'>
            <Box className='w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('geofenceStatus.title')}</Typography>
              <BaseSelect
                options={geofenceStatusOptions}
                value={filter.geofenceStatus ?? ''}
                placeholder={t('select_geofence_status')}
                isDisplayAllOption
                onChange={(e) => {
                  const value = e.target.value;
                  dispatch(
                    personActions.setFilter({
                      geofenceStatus: value === '' ? undefined : Number(value),
                      pageIndex: PAGE_INDEX,
                    })
                  );
                }}
              />
            </Box>
            <Box className='w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('status.title')}</Typography>
              <BaseSelect
                options={statusOptions}
                value={filter.active ?? ''}
                placeholder={t('select_status')}
                isDisplayAllOption
                onChange={(e) => {
                  const value = e.target.value;
                  dispatch(
                    personActions.setFilter({ active: value === '' ? undefined : Number(value), pageIndex: PAGE_INDEX })
                  );
                }}
              />
            </Box>
            <Box className='w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('createDate')}</Typography>
              <Box className='flex justify-between items-center gap-3 max-[768px]:!flex-col'>
                <Box className='w-full'>
                  <BaseDatePicker
                    portalId='datePicker-portalId'
                    value={filter.startDate}
                    dateFormat='YYYY-MM-DD'
                    onChange={(date) =>
                      dispatch(
                        personActions.setFilter({
                          startDate: dayjs(date).startOf('day').format('YYYY-MM-DD'),
                          pageIndex: PAGE_INDEX,
                        })
                      )
                    }
                    placeholder={t('startDate')}
                    calendarProps={filter.endDate ? { maxDate: dayjs(filter.endDate).toDate() } : {}}
                    className='w-full'
                  />
                </Box>
                <Box className='w-4 h-px bg-black hidden lg:block'></Box>
                <Box className='w-full'>
                  <BaseDatePicker
                    portalId='datePicker-portalId'
                    value={filter.endDate}
                    dateFormat='YYYY-MM-DD'
                    onChange={(date) =>
                      dispatch(
                        personActions.setFilter({
                          endDate: dayjs(date).endOf('day').format('YYYY-MM-DD'),
                          pageIndex: PAGE_INDEX,
                        })
                      )
                    }
                    placeholder={t('endDate')}
                    calendarProps={filter.startDate ? { minDate: dayjs(filter.startDate).toDate() } : {}}
                    className='w-full'
                  />
                </Box>
                <Box className='flex-1'>
                  <Button
                    className='border border-solid border-[#123B6D] bg-[#DCE8F5] rounded-xl text-base font-bold text-[#123B6D] w-[132px] hover:bg-[#0E2F58] hover:opacity-80 hover:text-white'
                    onClick={handleClearFilter}
                  >
                    {t('clear', { ns: 'admin/common' })}
                  </Button>
                </Box>
              </Box>
            </Box>
          </BaseRow>
        </Box>
      </BaseRow>

      <Box className='px-6'>
        <PersonList
          onOpenEdit={handleOpenEdit}
          onOpenActive={(row) => {
            setEditData(row);
            setIsOpenConfirmActivePersonDialog(true);
          }}
          onAssignDevice={handleOpenAssignDeviceModal}
          onExchangeDevice={handleOpenExchangeDeviceModal}
          onUnassignDevice={handleOpenUnassignDeviceModal}
          onCallDevice={(row: IPerson) => {
            openCallDeviceModal(row);
          }}
          onSendMessage={handleOpenSendMessageSingle}
          canSendMessage={isXaPhuongRole}
          onAssignLocation={(row: IPerson) => {
            setEditData(row);
            setIsOpenAssignLocationDialog(true);
          }}
          onUnAssignLocation={(row: IPerson) => {
            setEditData(row);
            setIsOpenUnAssignLocationDialog(true);
          }}
        />
      </Box>

      <AddImeiDialog open={isOpenAddImeiDialog} setOpen={(open) => setIsOpenAddImeiDialog(open)} />

      {isOpenEditPersonDialog && (
        <EditPersonDialog
          data={editData}
          currentUser={currentUser}
          open={isOpenEditPersonDialog}
          mode={editPersonDialogMode}
          setOpen={handleSetOpenEditDialog}
        />
      )}

      <AssignDeviceModal
        isOpen={isOpenAssignDeviceModal}
        onClose={handleCloseAssignDeviceModal}
        personId={Number(assignDevicePerson?.id) || 0}
        cccd={assignDevicePerson?.cccd ? String(assignDevicePerson.cccd) : ''}
        fullName={assignDevicePerson?.full_name ? String(assignDevicePerson.full_name) : ''}
        onSuccess={() => dispatch(personActions.changeNeedRefreshData(true))}
      />

      <ExchangeDeviceModal
        isOpen={isOpenExchangeDeviceModal}
        onClose={handleCloseExchangeDeviceModal}
        personId={Number(assignDevicePerson?.id) || 0}
        cccd={assignDevicePerson?.cccd ? String(assignDevicePerson.cccd) : ''}
        fullName={assignDevicePerson?.full_name ? String(assignDevicePerson.full_name) : ''}
        currentImei={assignDevicePerson?.imei ? String(assignDevicePerson.imei) : ''}
        onSuccess={() => dispatch(personActions.changeNeedRefreshData(true))}
      />

      <UnassignDeviceModal
        isOpen={isOpenUnassignDeviceModal}
        onClose={handleCloseUnassignDeviceModal}
        personId={Number(assignDevicePerson?.id) || 0}
        cccd={assignDevicePerson?.cccd ? String(assignDevicePerson.cccd) : ''}
        fullName={assignDevicePerson?.full_name ? String(assignDevicePerson.full_name) : ''}
        currentImei={assignDevicePerson?.imei ? String(assignDevicePerson.imei) : ''}
        onSuccess={() => dispatch(personActions.changeNeedRefreshData(true))}
      />

      {isOpenAssignLocationDialog && (
        <AssignLocationDialog
          data={editData}
          currentUser={currentUser}
          open={isOpenAssignLocationDialog}
          setOpen={handleSetOpenAssignLocationDialog}
        />
      )}

      {isOpenUnAssignLocationDialog && (
        <UnAssignLocationDialog
          data={editData}
          currentUser={currentUser}
          open={isOpenUnAssignLocationDialog}
          setOpen={handleSetOpenUnAssignLocationDialog}
        />
      )}

      {isOpenConfirmActivePersonDialog && (
        <ConfirmDialog
          open={isOpenConfirmActivePersonDialog}
          title={t('active_person.title', {
            name: editData?.active ? t('active_person.block.title') : t('active_person.open.title'),
          })}
          description={editData?.active ? t('active_person.block.message') : t('active_person.open.message')}
          confirm={editData?.active ? t('active_person.block.button') : t('active_person.open.button')}
          btnConfirmClassName={editData?.active ? '' : '!bg-[#123B6D]'}
          onConfirm={handleActivePerson}
          onCancel={() => setIsOpenConfirmActivePersonDialog(false)}
        />
      )}

      <Dialog open={isOpenMessageDialog} onClose={handleCloseMessageDialog} fullWidth maxWidth='sm'>
        <DialogTitle className='bg-white'>
          <Box className='flex items-center justify-between'>
            <Typography className='text-xl font-semibold text-[#161A1F]'>
              {sendMessageType === 'single' ? t('message.title.single') : t('message.title.all')}
            </Typography>
            <IconButton className='p-1' onClick={handleCloseMessageDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {sendMessageType === 'single' && (
            <Box className='mb-2'>
              <InputLabel
                shrink={false}
                className='static !transform-none h-5 flex items-center font-medium text-sm leading-[140%] tracking-[0.02em] text-[#161A1F] mb-0'
              >
                Nguoi nhan: {cast(messageTargetPerson?.full_name) || cast(messageTargetPerson?.cccd)}
              </InputLabel>
            </Box>
          )}
          <Box>
            <TextareaAutosize
              minRows={4}
              maxRows={8}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder={t('message.placeholder')}
              style={{
                width: '100%',
                padding: '16px 14px',
                fontSize: '16px',
                lineHeight: '1.4375',
                fontFamily: 'inherit',
                border: '1px solid #c4c4c4',
                borderRadius: '4px',
                resize: 'none',
                boxSizing: 'border-box',
                overflowY: 'auto',
                outline: 'none',
              }}
            />
          </Box>
          <Box className='flex justify-between gap-3 mt-5'>
            <Button
              variant='outlined'
              className='w-full bg-[#D7DEE7] border-0 !border-inherit text-[#404854] text-base font-semibold'
              onClick={handleCloseMessageDialog}
            >
              {t('cancel', { ns: 'admin/common' })}
            </Button>
            <Button
              variant='contained'
              className='w-full bg-[#123B6D] text-base font-semibold'
              onClick={handleSubmitSendMessage}
              disabled={isSendingMessage}
            >
              {isSendingMessage ? t('message.sending') : t('message.send')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default memo(Person);
